import { useContext, useState } from "react";

import { TenantContext } from "@nens/ranalib";
import { useRunOnceWhen } from "@nens/react/hooks";

import { useFileDirectoryQuery } from "@/queries/query-file";
import { useJobListQuery } from "@/queries/query-job";
import { useProcessByTagQuery } from "@/queries/query-process";
import { useProjectUserListQuery } from "@/queries/query-project-users";
import { ProjectContext } from "@/stores/project-provider";

import { usePutProject } from "../api/put-project";

export type OnboardingStatus = "done" | "loading" | "not_started";

export type OnboardingStatusMap = {
  collaborators: OnboardingStatus;
  files: OnboardingStatus;
  studyArea: OnboardingStatus;
};

export const useOnboardingStatus = () => {
  const project = useContext(ProjectContext);
  const authInfo = useContext(TenantContext);
  const userId = !authInfo.isPublic ? authInfo.user.id : undefined;
  if (project.showOnboarding === false) {
    console.warn(
      "Project showOnboarding is false, then this hook should not be used. Please check the logic of using this hook."
    );
  }

  const projectUsersQuery = useProjectUserListQuery();
  const filesQuery = useFileDirectoryQuery("");
  const { data: defineStudyAreaProcess } = useProcessByTagQuery("define_study_area");
  const jobListQuery = useJobListQuery({
    disableRefetching: true,
    enabled: !!defineStudyAreaProcess,
    processId: defineStudyAreaProcess?.id,
  });

  const { mutate } = usePutProject();
  const [showProjectWelcome, setShowProjectWelcome] = useState(true);

  const setDoneOnboarding = () => {
    setShowProjectWelcome(false);
    mutate({ showOnboarding: false });
  };

  let collaboratorsStatus: OnboardingStatus;
  if (!projectUsersQuery.isSuccess) {
    collaboratorsStatus = "loading";
  } else if (
    project.contributors.some((contributor) => contributor.id !== userId) ||
    projectUsersQuery.data.items.some(({ user }) => user.id !== userId)
  ) {
    collaboratorsStatus = "done";
  } else {
    collaboratorsStatus = "not_started";
  }

  let filesStatus: OnboardingStatus;
  if (!filesQuery.isSuccess) {
    filesStatus = "loading";
  } else if (filesQuery.data.items.length > 0) {
    filesStatus = "done";
  } else {
    filesStatus = "not_started";
  }

  let studyAreaStatus: OnboardingStatus;
  if (!jobListQuery.isSuccess) {
    studyAreaStatus = "loading";
  } else if (jobListQuery.data.items.length > 0) {
    studyAreaStatus = "done";
  } else {
    studyAreaStatus = "not_started";
  }

  const onboardingStatusMap: OnboardingStatusMap = {
    collaborators: collaboratorsStatus,
    files: filesStatus,
    studyArea: studyAreaStatus,
  };

  useRunOnceWhen(
    Object.values(onboardingStatusMap).every((status) => status === "done"),
    setDoneOnboarding
  );

  return { onboardingStatusMap, setDoneOnboarding, showProjectWelcome };
};
